import {
  combineReducers,
  configureStore,
  createSlice,
  type PayloadAction,
  type Middleware,
  type Reducer,
} from '@reduxjs/toolkit';
import { createDynamicMiddleware } from '@reduxjs/toolkit/react';
import { useDispatch, useSelector, type TypedUseSelectorHook } from 'react-redux';

/**
 * The shell owns the Redux store root. It ships with a single static slice
 * (platform boot state); federated feature apps inject their slices AND their
 * RTK Query middleware at mount via injectReducer/addMiddleware, both exposed
 * through the federation module 'shell/store'.
 */
export interface PlatformState {
  /** True once the shell finished boot wiring (api client configured). */
  ready: boolean;
}

const initialPlatformState: PlatformState = { ready: false };

const platformSlice = createSlice({
  name: 'platform',
  initialState: initialPlatformState,
  reducers: {
    platformReady(state) {
      state.ready = true;
    },
  },
});

export const { platformReady } = platformSlice.actions;

/** Tenant UI language, read once from /api/config after sign-in (see ../i18n.ts). */
export interface ConfigState {
  status: 'idle' | 'loading' | 'done' | 'failed';
  language: string | null;
}

const initialConfigState: ConfigState = { status: 'idle', language: null };

const configSlice = createSlice({
  name: 'config',
  initialState: initialConfigState,
  reducers: {
    configLoading(state) {
      state.status = 'loading';
    },
    configLoaded(state, action: PayloadAction<string | null>) {
      state.status = 'done';
      state.language = action.payload;
    },
    configFailed(state) {
      state.status = 'failed';
    },
    configReset() {
      return initialConfigState;
    },
  },
});

export const { configLoading, configLoaded, configFailed, configReset } = configSlice.actions;

/** Static shape plus whatever feature apps inject at runtime. */
export interface RootState {
  platform: PlatformState;
  config: ConfigState;
  [injectedSlice: string]: unknown;
}

const staticReducers = {
  platform: platformSlice.reducer,
  config: configSlice.reducer,
};

const injectedReducers: Record<string, Reducer> = {};

function buildRootReducer(): Reducer<RootState> {
  return combineReducers({
    ...staticReducers,
    ...injectedReducers,
  }) as unknown as Reducer<RootState>;
}

const dynamicMiddlewareInstance = createDynamicMiddleware();

export const store = configureStore({
  reducer: buildRootReducer(),
  middleware: (getDefault) => getDefault().concat(dynamicMiddlewareInstance.middleware),
});

/**
 * Called by federated feature apps to add their state slices at runtime
 * (e.g. an RTK Query api reducer). Idempotent per key; static slices can
 * never be shadowed.
 */
export function injectReducer(key: string, reducer: Reducer): void {
  if (key in staticReducers || injectedReducers[key]) {
    return;
  }
  injectedReducers[key] = reducer;
  store.replaceReducer(buildRootReducer());
}

/**
 * Called by federated feature apps to add middleware at runtime
 * (e.g. an RTK Query api middleware). RTK's dynamic middleware dedupes,
 * so repeated registration of the same middleware is safe.
 */
export function addMiddleware(...middlewares: Middleware[]): void {
  dynamicMiddlewareInstance.addMiddleware(
    ...(middlewares as Parameters<typeof dynamicMiddlewareInstance.addMiddleware>)
  );
}

export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
